import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import '@business/assets/css/CustomDatePicker.css';

interface CustomDatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  onBlur?: () => void;
  label?: any;
  placeholder?: string;
  maxDate?: Date;
  minDate?: Date;
  dateFormat?: string;
  valueFormat?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  clearable?: boolean;
}

const DEFAULT_VALUE_FORMAT = 'MMM D, YYYY';

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  onBlur,
  label,
  placeholder = 'Select a date',
  maxDate = new Date(),
  minDate,
  dateFormat = 'MMM d, yyyy',
  valueFormat = DEFAULT_VALUE_FORMAT,
  disabled = false,
  error,
  className = '',
  inputClassName = '',
  required = false,
  showYearDropdown = true,
  showMonthDropdown = true,
  yearDropdownItemNumber = 100,
  scrollableYearDropdown = false,
  clearable = disabled ? false : true
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const parsedDate = useMemo(() => {
    if (!value) return null;

    const strictParsed = moment(
      value,
      [valueFormat, 'MMM D, YYYY', 'MMM DD, YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'],
      true
    );

    if (strictParsed.isValid()) {
      return strictParsed.toDate();
    }

    const fallbackParsed = moment(value);
    return fallbackParsed.isValid() ? fallbackParsed.toDate() : null;
  }, [value, valueFormat]);

  const wrapperStateClass = [
    'cdp-wrapper',
    isFocused ? 'focused' : '',
    value ? 'has-value' : '',
    error ? 'has-error' : '',
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperStateClass}>
      {label && (
        <label className="cdp-label">
          {label}
          {required && <span className="requiredAsterisk">*</span>}
        </label>
      )}

      <div className="cdp-input-container">
        <span className="cdp-icon" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>

        <DatePicker
          selected={parsedDate}
          onChange={(date: Date | null) => {
            onChange(date ? moment(date).format(valueFormat) : '');
          }}
          onBlur={onBlur}
          maxDate={maxDate}
          minDate={minDate}
          dateFormat={dateFormat}
          placeholderText={placeholder}
          showYearDropdown={showYearDropdown}
          showMonthDropdown={showMonthDropdown}
          dropdownMode="select"
          yearDropdownItemNumber={yearDropdownItemNumber}
          scrollableYearDropdown={scrollableYearDropdown}
          disabled={disabled}
          onCalendarOpen={() => setIsFocused(true)}
          onCalendarClose={() => setIsFocused(false)}
          popperClassName="cdp-popper"
          wrapperClassName="w-full"
          className={`cdp-input ${inputClassName}`.trim()}
        />

        {clearable && value && !disabled && (
          <button
            type="button"
            className="cdp-clear"
            onClick={() => onChange('')}
            aria-label="Clear date">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {error && <p className="cdp-error">{error}</p>}
    </div>
  );
};

export default CustomDatePicker;
